import { securityGroupListSchema, securityGroupSchema, securityGroupMemberListSchema, securityGroupMemberSchema, mySecurityGroupSchema, newSecurityGroupSchema, updateSecurityGroupSchema, addSecurityGroupMemberSchema, } from '../contracts/security-groups';
import { organizationIdSchema, } from '../contracts/organizations';
import { parseSchema } from '../errors';
/**
 * List an organization's security groups.
 *
 * Soft-deleted groups are excluded unless `includeDeleted` is set. That opt-in
 * exists for the alerts Security Group filter: alerts keep naming the group
 * that triggered them after deletion, so the filter must be able to offer a
 * deleted group or it cannot match rows that are visibly on screen. Audit Logs
 * uses it too, so a change keeps naming the group it was made for. Callers
 * that opt in should mark them — `deletedAt` is populated.
 */
export async function listSecurityGroups(client, organizationId, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/security-groups`, options?.includeDeleted ? { params: { includeDeleted: true } } : undefined);
    return parseSchema(securityGroupListSchema, response);
}
/**
 * Get a single security group by id.
 */
export async function getSecurityGroup(client, organizationId, groupId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}`);
    return parseSchema(securityGroupSchema, response);
}
/**
 * The security group the CALLING user belongs to, or `{ group: null }`.
 *
 * Readable by any role, unlike `listSecurityGroups` — it is how a non-admin
 * discovers which policies apply to them (KIR-4334).
 */
export async function getMySecurityGroup(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/security-groups/me`);
    return parseSchema(mySecurityGroupSchema, response);
}
/**
 * Create a security group in an organization.
 */
export async function createSecurityGroup(client, organizationId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(newSecurityGroupSchema, data);
    const response = await client.post(`/organizations/${validatedId}/security-groups`, validatedData);
    return parseSchema(securityGroupSchema, response);
}
/**
 * Update a security group's name and/or description.
 */
export async function updateSecurityGroup(client, organizationId, groupId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(updateSecurityGroupSchema, data);
    const response = await client.put(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}`, validatedData);
    return parseSchema(securityGroupSchema, response);
}
/**
 * Delete a security group. Memberships and group-scoped policy rows are removed
 * via cascade, so members fall back to organization defaults.
 */
export async function deleteSecurityGroup(client, organizationId, groupId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    await client.delete(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}`);
}
/**
 * List the members of a security group. Returned `userId`s are Descope IDs.
 */
export async function listSecurityGroupMembers(client, organizationId, groupId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/members`);
    return parseSchema(securityGroupMemberListSchema, response);
}
/**
 * Add a user (by Descope ID) to a security group. Moves the user if they already
 * belong to another group in the org.
 */
export async function addSecurityGroupMember(client, organizationId, groupId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(addSecurityGroupMemberSchema, data);
    const response = await client.post(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/members`, validatedData);
    return parseSchema(securityGroupMemberSchema, response);
}
/**
 * Remove a user (by Descope ID) from a security group.
 */
export async function removeSecurityGroupMember(client, organizationId, groupId, userId) {
    const validatedOrgId = parseSchema(organizationIdSchema, organizationId);
    await client.delete(`/organizations/${validatedOrgId}/security-groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`);
}
//# sourceMappingURL=security-groups.js.map